/**
 * Detection Settings Component
 * Issue #381: Detection logic customization UI
 *
 * Allows users to view and customize the regex patterns used
 * for detecting diagram elements (API endpoints, database queries, etc.)
 */

import { useState, useCallback, useEffect } from 'react';
import { api, type DetectionPatternsResponse, type CategoryInfo } from '../../services/api';
import type { EffectivePattern, TestPatternResult } from '@veodiagram/shared';
import { getCategoryDisplayName } from '@veodiagram/shared';

interface DetectionSettingsProps {
  projectId: string;
  onClose: () => void;
}

export function DetectionSettings({ projectId, onClose }: DetectionSettingsProps) {
  const [patterns, setPatterns] = useState<EffectivePattern[]>([]);
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [stats, setStats] = useState({ totalPatterns: 0, customizedCount: 0, disabledCount: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Edit modal state
  const [editingPattern, setEditingPattern] = useState<EffectivePattern | null>(null);
  const [editRegex, setEditRegex] = useState('');
  const [editConfidence, setEditConfidence] = useState(0.8);
  const [isSaving, setIsSaving] = useState(false);

  // Test panel state
  const [testCode, setTestCode] = useState('');
  const [testResult, setTestResult] = useState<TestPatternResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  // Load patterns
  const loadPatterns = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data: DetectionPatternsResponse = await api.getDetectionPatterns(projectId);
      setPatterns(data.patterns);
      setCategories(data.categoryInfo);
      setStats(data.stats);
      if (!selectedCategory && data.categoryInfo.length > 0) {
        setSelectedCategory(data.categoryInfo[0].key);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load patterns');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, selectedCategory]);

  useEffect(() => {
    loadPatterns();
  }, [loadPatterns]);

  // Toggle pattern enabled/disabled
  const handleTogglePattern = useCallback(async (pattern: EffectivePattern) => {
    try {
      await api.updateDetectionPattern(projectId, pattern.pattern.key, {
        enabled: !pattern.enabled,
      });
      await loadPatterns();
      setSuccess(`Pattern ${pattern.enabled ? 'disabled' : 'enabled'}`);
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update pattern');
    }
  }, [projectId, loadPatterns]);

  // Open edit modal
  const handleEditPattern = useCallback((pattern: EffectivePattern) => {
    setEditingPattern(pattern);
    setEditRegex(pattern.effectiveRegex);
    setEditConfidence(pattern.effectiveConfidence);
    setTestResult(null);
    setTestCode('');
  }, []);

  // Save pattern changes
  const handleSavePattern = useCallback(async () => {
    if (!editingPattern) return;

    setIsSaving(true);
    setError(null);

    try {
      await api.updateDetectionPattern(projectId, editingPattern.pattern.key, {
        customRegex: editRegex !== editingPattern.pattern.regex ? editRegex : null,
        confidence: editConfidence !== editingPattern.pattern.confidence ? editConfidence : null,
      });
      await loadPatterns();
      setEditingPattern(null);
      setSuccess('Pattern updated successfully');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save pattern');
    } finally {
      setIsSaving(false);
    }
  }, [projectId, editingPattern, editRegex, editConfidence, loadPatterns]);

  // Reset pattern to default
  const handleResetPattern = useCallback(async (patternKey: string) => {
    if (!confirm('Reset this pattern to its default?')) return;

    try {
      await api.resetDetectionPattern(projectId, patternKey);
      await loadPatterns();
      if (editingPattern?.pattern.key === patternKey) {
        setEditingPattern(null);
      }
      setSuccess('Pattern reset to default');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset pattern');
    }
  }, [projectId, editingPattern, loadPatterns]);

  // Reset all patterns
  const handleResetAll = useCallback(async () => {
    if (!confirm('Reset ALL patterns to defaults? This will remove all customizations.')) return;

    try {
      await api.resetAllDetectionPatterns(projectId);
      await loadPatterns();
      setSuccess('All patterns reset to defaults');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset patterns');
    }
  }, [projectId, loadPatterns]);

  // Test pattern
  const handleTestPattern = useCallback(async () => {
    if (!testCode.trim()) return;

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await api.testDetectionPattern(projectId, {
        regex: editRegex,
        flags: editingPattern?.pattern.flags || 'gi',
        sampleCode: testCode,
      });
      setTestResult(result);
    } catch (err) {
      setTestResult({
        valid: false,
        error: err instanceof Error ? err.message : 'Test failed',
        matchCount: 0,
        matches: [],
      });
    } finally {
      setIsTesting(false);
    }
  }, [projectId, editRegex, editingPattern, testCode]);

  // Get patterns for selected category
  const filteredPatterns = patterns.filter(
    p => p.pattern.category === selectedCategory
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🔍</span>
            <div>
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                Detection Settings
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {stats.totalPatterns} patterns, {stats.customizedCount} customized, {stats.disabledCount} disabled
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}
        {success && (
          <div className="mx-4 mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-sm text-green-600 dark:text-green-400">{success}</p>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {isLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
            </div>
          ) : (
            <>
              {/* Category Sidebar */}
              <div className="w-48 border-r border-slate-200 dark:border-slate-700 p-2 overflow-y-auto">
                {categories.map(cat => {
                  const catPatterns = patterns.filter(p => p.pattern.category === cat.key);
                  const customized = catPatterns.filter(p => p.isCustomized).length;
                  const disabled = catPatterns.filter(p => !p.enabled).length;

                  return (
                    <button
                      key={cat.key}
                      onClick={() => setSelectedCategory(cat.key)}
                      className={`w-full text-left px-3 py-2 rounded-lg mb-1 transition-colors ${
                        selectedCategory === cat.key
                          ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300'
                          : 'hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      <div className="text-sm font-medium">{cat.displayName}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {catPatterns.length} patterns
                        {(customized > 0 || disabled > 0) && (
                          <span className="ml-1">
                            ({customized > 0 && `${customized} custom`}
                            {customized > 0 && disabled > 0 && ', '}
                            {disabled > 0 && `${disabled} off`})
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Pattern List */}
              <div className="flex-1 overflow-y-auto p-4">
                {selectedCategory && (
                  <>
                    <div className="mb-4">
                      <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        {getCategoryDisplayName(selectedCategory as any)}
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        {categories.find(c => c.key === selectedCategory)?.description}
                      </p>
                    </div>

                    <div className="space-y-2">
                      {filteredPatterns.map(pattern => (
                        <div
                          key={pattern.pattern.key}
                          className={`p-3 border rounded-lg transition-colors ${
                            pattern.enabled
                              ? 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
                              : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 opacity-60'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="text-sm font-medium text-slate-800 dark:text-slate-200">
                                  {pattern.pattern.name}
                                </h4>
                                {pattern.isCustomized && (
                                  <span className="px-1.5 py-0.5 text-xs bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded">
                                    Customized
                                  </span>
                                )}
                                {pattern.pattern.framework && (
                                  <span className="px-1.5 py-0.5 text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded">
                                    {pattern.pattern.framework}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                {pattern.pattern.description}
                              </p>
                              <div className="mt-2 font-mono text-xs text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 p-2 rounded overflow-x-auto">
                                {pattern.effectiveRegex}
                              </div>
                              {pattern.pattern.example && (
                                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                                  Example: <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">{pattern.pattern.example}</code>
                                </p>
                              )}
                            </div>

                            <div className="flex items-center gap-2 ml-4">
                              <span className="text-xs text-slate-500">
                                {Math.round(pattern.effectiveConfidence * 100)}%
                              </span>
                              <button
                                onClick={() => handleTogglePattern(pattern)}
                                className={`relative w-10 h-6 rounded-full transition-colors ${
                                  pattern.enabled
                                    ? 'bg-indigo-500'
                                    : 'bg-slate-300 dark:bg-slate-600'
                                }`}
                              >
                                <span
                                  className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                    pattern.enabled ? 'left-5' : 'left-1'
                                  }`}
                                />
                              </button>
                              <button
                                onClick={() => handleEditPattern(pattern)}
                                className="p-1.5 text-slate-400 hover:text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded"
                                title="Edit pattern"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              {pattern.isCustomized && (
                                <button
                                  onClick={() => handleResetPattern(pattern.pattern.key)}
                                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                  title="Reset to default"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={handleResetAll}
            className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400"
          >
            Reset All to Defaults
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg"
          >
            Close
          </button>
        </div>

        {/* Edit Modal */}
        {editingPattern && (
          <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
              <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
                <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                  Edit Pattern: {editingPattern.pattern.name}
                </h3>
                <button
                  onClick={() => setEditingPattern(null)}
                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 rounded"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Regex Pattern
                  </label>
                  <textarea
                    value={editRegex}
                    onChange={(e) => setEditRegex(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 text-sm font-mono border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Default: <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">{editingPattern.pattern.regex}</code>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Confidence Score: {Math.round(editConfidence * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={editConfidence}
                    onChange={(e) => setEditConfidence(parseFloat(e.target.value))}
                    className="w-full"
                  />
                </div>

                {/* Test Panel */}
                <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Test Pattern
                  </label>
                  <textarea
                    value={testCode}
                    onChange={(e) => setTestCode(e.target.value)}
                    rows={4}
                    placeholder="Paste sample code to test the pattern..."
                    className="w-full px-3 py-2 text-sm font-mono border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    onClick={handleTestPattern}
                    disabled={isTesting || !testCode.trim()}
                    className="mt-2 px-3 py-1.5 text-sm bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50"
                  >
                    {isTesting ? 'Testing...' : 'Test Pattern'}
                  </button>

                  {testResult && (
                    <div className={`mt-3 p-3 rounded-lg ${
                      testResult.valid
                        ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                        : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                    }`}>
                      {testResult.valid ? (
                        <>
                          <p className="text-sm text-green-700 dark:text-green-300">
                            Found {testResult.matchCount} match{testResult.matchCount !== 1 ? 'es' : ''}
                          </p>
                          {testResult.matches.length > 0 && (
                            <ul className="mt-2 text-xs text-green-600 dark:text-green-400 space-y-1">
                              {testResult.matches.slice(0, 5).map((m, i) => (
                                <li key={i}>
                                  Line {m.line}: <code className="bg-green-100 dark:bg-green-800 px-1 rounded">{m.text}</code>
                                </li>
                              ))}
                              {testResult.matches.length > 5 && (
                                <li>...and {testResult.matches.length - 5} more</li>
                              )}
                            </ul>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-red-600 dark:text-red-400">
                          {testResult.error || 'Invalid regex pattern'}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-200 dark:border-slate-700">
                <button
                  onClick={() => setEditingPattern(null)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePattern}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DetectionSettings;
